import { Storage, STORAGE_KEYS } from "../utils/store";
import { Listenable } from "./listenable";
import translator from '../utils/translation';

class WebSocketEngine extends Listenable {
    #sockets = new Map();
    #urls = new Map();
    #options = new Map();
    #reconnectTimeouts = new Map();
    #attempts = new Map();
    #RECONNECT_DELAY = 5000;
    #MAX_RECONNECT_DELAY = 30000;
    #MAX_RECONNECT_ATTEMPTS = 6;
    #DEFAULT_AUTH_CLOSE_CODES = [4001];

    connect(name, url, options = {}) {
        this.#urls.set(name, url);
        this.#options.set(name, options);
        this.#clearReconnectTimeout(name);

        if (this.#sockets.has(name)) {
            const oldWs = this.#sockets.get(name);
            oldWs.onclose = null;
            oldWs.onerror = null;
            oldWs.close();
        }

        const attempts = (this.#attempts.get(name) || 0) + 1;
        this.#attempts.set(name, attempts);

        const ws = new WebSocket(this.#resolveUrl(name));

        ws.onopen = () => {
            console.debug(`[WS] Connected to ${name}`);
            this.#clearReconnectTimeout(name);
            this.#attempts.set(name, 0);
        };

        ws.onmessage = (event) => {
            if (!event.isTrusted) {
                console.warn("Message received from untrusted source.\nIt will be ignored.");
                return;
            }

            const message = JSON.parse(event.data);
            //console.debug(`[WS] Message received from ${name}`, message);
            this.emit(name, message);
        };

        ws.onerror = (err) => {
            if (this.#attempts.get(name) === 2) {
                M.toast({html: translator.t('error.websocket', { name }), classes: 'red'});
            }
            console.error(`[WS] Error on ${name}:`, err);
            this.emit(`${name}:error`, err);
        };

        ws.onclose = (event) => {
            if (this.#sockets.get(name) === ws) {
                this.#sockets.delete(name);
            }

            if (event.wasClean) {
                console.debug(`[WS] Connection closed cleanly for ${name}`);
                return;
            }

            if (this.#isAuthCloseCode(name, event.code)) {
                console.warn(`[WS] Authentication rejected for ${name} (code ${event.code}).`);
                this.emit(`${name}:auth-error`, event);
                return;
            }

            const maxAttempts = this.#maxAttempts(name);
            if (this.#attempts.get(name) >= maxAttempts) {
                console.error(`[WS] Reconnection to ${name} failed after ${maxAttempts} attempts.`);
                this.emit(`${name}:reconnect-failed`, event);
                return;
            }

            const delay = this.#reconnectDelay(name);
            console.warn(`[WS] Connection lost for ${name}. Attempting reconnection in ${delay}ms...`);
            this.#scheduleReconnect(name, delay);
        };

        this.#sockets.set(name, ws);
    }

    #resolveUrl(name) {
        const url = this.#urls.get(name);
        return typeof url === 'function' ? url() : url;
    }

    #maxAttempts(name) {
        return this.#options.get(name)?.maxAttempts ?? this.#MAX_RECONNECT_ATTEMPTS;
    }

    #isAuthCloseCode(name, code) {
        const codes = this.#options.get(name)?.authCloseCodes ?? this.#DEFAULT_AUTH_CLOSE_CODES;
        return codes.includes(code);
    }

    #reconnectDelay(name) {
        const attempts = this.#attempts.get(name) || 1;
        const delay = this.#RECONNECT_DELAY * Math.pow(2, attempts - 1);
        return Math.min(delay, this.#MAX_RECONNECT_DELAY);
    }

    #scheduleReconnect(name, delay = this.#RECONNECT_DELAY) {
        this.#clearReconnectTimeout(name);

        const timeoutId = setTimeout(() => {
            if (this.#urls.has(name)) {
                console.debug(`[WS] Reconnecting to ${name}...`);
                this.connect(name, this.#urls.get(name), this.#options.get(name));
            }
        }, delay);

        this.#reconnectTimeouts.set(name, timeoutId);
    }

    #clearReconnectTimeout(name) {
        if (this.#reconnectTimeouts.has(name)) {
            clearTimeout(this.#reconnectTimeouts.get(name));
            this.#reconnectTimeouts.delete(name);
        }
    }

    #clearListeners(name) {
        this._listeners.delete(name);
        this._listeners.delete(`${name}:error`);
        this._listeners.delete(`${name}:auth-error`);
        this._listeners.delete(`${name}:reconnect-failed`);
    }

    close(name) {
        this.#urls.delete(name);
        this.#options.delete(name);
        this.#clearReconnectTimeout(name);
        this.#attempts.delete(name);
        this.#sockets.get(name)?.close();
        this.#sockets.delete(name);
        this.#clearListeners(name);
    }

    closeAll() {
        Array.from(this.#reconnectTimeouts.keys()).forEach(name => this.#clearReconnectTimeout(name));
        this.#urls.clear();
        this.#options.clear();
        this.#attempts.clear();

        Array.from(this.#sockets.keys())
            .forEach(key => this.#sockets.get(key)?.close());

        this.#sockets.clear();
        this._listeners.clear();
    }
}

const websocketEngine = new WebSocketEngine();

export default websocketEngine;
export const GendLocWebsockets = {
    notification: {
        name: 'notification',
        get url() {
            let url = process.env.GENDLOC_BACKEND_URL.replace('http', 'ws');

            if (!url.startsWith('ws')) {
                url = `${self.location.origin.replace('http', 'ws')}/${url}`;
            }

            return `${url}/notification/?token=${Storage.get(STORAGE_KEYS.JWT)}`;
        }
    }
};
